import { useMemo, useState, useEffect } from "react";
import { useCaseEvidenceInventory, type InventoryItem } from "../../hooks/useCaseEvidenceInventory";
import { useToggleInclusion } from "../../hooks/useToggleInclusion";

/**
 * Evidence inventory — every ingested piece with a reversible on/off switch.
 * Toggling ripples to the Saga, the Web of Wyrd, and the scoreboard (any view
 * that respects evidence_inclusion). A "piece" is one item: a thread, the text
 * conversation, or a filing.
 */
export default function EvidenceInventory({ caseId }: { caseId: string | undefined }) {
  const { data: items, isLoading, error } = useCaseEvidenceInventory(caseId);
  const toggle = useToggleInclusion();

  const { included, total } = useMemo(() => {
    const list = items ?? [];
    return { included: list.filter((i) => i.included).length, total: list.length };
  }, [items]);

  const groups = useMemo(() => {
    const m = new Map<string, InventoryItem[]>();
    for (const it of items ?? []) {
      const arr = m.get(it.category) ?? [];
      arr.push(it);
      m.set(it.category, arr);
    }
    const order = ["communications", "trust_corporate", "financial", "public_regulatory", "document"];
    return [...m.entries()].sort(
      (a, b) => ((order.indexOf(a[0]) + 1) || 99) - ((order.indexOf(b[0]) + 1) || 99),
    );
  }, [items]);

  return (
    <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3">
        <span className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-dim">evidence inventory</span>
        <span className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
          {total ? `${included} of ${total} in play` : "—"}
        </span>
      </div>

      <div className="px-4 py-4">
        {isLoading && <p className="font-display text-base text-bone-mute">// counting the corpus…</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && (items?.length ?? 0) === 0 && (
          <p className="font-display text-base text-bone-mute">// no evidence ingested yet</p>
        )}
        {groups.map(([cat, rows]) => (
          <div key={cat} className="mb-3">
            <p className="mb-1.5 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute/70">
              {CAT_LABEL[cat] ?? cat} · {rows.length}
            </p>
            <ul className="space-y-1.5">
              {rows.map((it) => (
                <InventoryRow
                  key={`${it.item_kind}:${it.item_id}`}
                  item={it}
                  busy={toggle.isPending}
                  onToggle={(next) =>
                    caseId &&
                    toggle.mutate({ caseId, itemKind: it.item_kind, itemId: it.item_id, included: next })
                  }
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function prettyLabel(it: InventoryItem): string {
  if (it.sub_kind === "text_message") return "Text-message conversation";
  return it.label.replace(/\.pdf$/i, "");
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

const CAT_LABEL: Record<string, string> = {
  communications: "Communications — email & text",
  trust_corporate: "Trust, Will & Corporate",
  financial: "Accounting & Financial",
  public_regulatory: "Public Records & Regulatory",
  document: "Documents",
};

const KIND_CHIP: Record<string, string> = {
  email: "text-blue-bright border-blue-bright/40",
  text_message: "text-bone-dim border-bone-mute/40",
  filing: "text-ember-bright border-ember-bright/40",
};

function InventoryRow({
  item,
  busy,
  onToggle,
}: {
  item: InventoryItem;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  const [on, setOn] = useState(item.included);
  useEffect(() => { setOn(item.included); }, [item.included]);
  const flip = () => { const next = !on; setOn(next); onToggle(next); };
  return (
    <li
      className={`flex items-center gap-3 rounded border px-3 py-2 transition-colors ${
        on ? "border-steel-dark/70 bg-black/20" : "border-steel-dark/40 bg-black/10 opacity-55"
      }`}
    >
      <button
        type="button"
        disabled={busy}
        onClick={flip}
        aria-label={on ? "exclude this piece" : "include this piece"}
        className={`relative h-4 w-7 shrink-0 rounded-full transition-colors duration-200 ${
          on ? "bg-ember-bright/70" : "bg-steel-dark"
        }`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-bone transition-transform duration-200 ${
            on ? "translate-x-3" : "translate-x-0"
          }`}
        />
      </button>

      <div className="min-w-0 flex-1">
        <p className={`truncate font-display text-[13px] ${on ? "text-bone" : "text-bone-mute line-through"}`}>
          {prettyLabel(item)}
        </p>
        <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">
          {item.message_count} {item.item_kind === "filing" ? "filing" : "msgs"} · {fmt(item.first_at)}
          {item.last_at && item.last_at !== item.first_at ? ` – ${fmt(item.last_at)}` : ""}
        </p>
      </div>

      <span className={`shrink-0 rounded border px-1.5 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] ${KIND_CHIP[item.sub_kind] ?? "text-bone-mute border-steel-dark"}`}>
        {item.sub_kind === "text_message" ? "texts" : item.sub_kind}
      </span>

      {!on && (
        <span className="shrink-0 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/60">excluded</span>
      )}
    </li>
  );
}
